import { useState, useEffect, useRef, useMemo } from 'react'
import Spinner from 'react-bootstrap/Spinner'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { ENVIO_A_CARGO_COMPRADOR } from '../../../constants/mediosEnviosPagos.js'
import { ESTADO_PUBLICADA, ESTADO_VENCIDA } from '../../../constants/publicaciones.js'
import { aplicarOrdenamiento } from '../../../helpers/aplicarOrdenamiento.js'
import { validarRangoFechas } from '../../../helpers/dateValidation.js'

import { aplicarFiltrosGenerico } from '../../../helpers/filtros/aplicarFiltrosGenerico'
import { MAPA_PUBLICACIONES } from '../../../helpers/filtros/mapaPublicaciones'
import { useAuth } from '../../../hooks/useAuth'
import { useImagenesPublicacion } from '../../../hooks/useImagenesPublicacion.js'
import { setFiltroLocal, setOrdenamiento } from '../../../slices/filterSlice.js'
import { usePaginacion } from '../../hooks/usePaginacion'
import Publicacion from '../../publicaciones/Publicacion'
import { FiltrosContainer } from '../../reutilizables/filtros/FiltrosContainer'
import { OrdenarPor } from '../../reutilizables/ordenamiento/OrdenarPor'
import { VistaCuadricula } from '../../reutilizables/ordenamiento/VistaCuadricula'
import { Paginacion } from '../../reutilizables/paginacion/Paginacion'
import {
  buscarListaPorEmpresa as buscarListaEnvioPorEmpresa,
  buscarListaPorVendedor as buscarListaEnvioPorVendedor
} from '../../servicios/listaEnvioService'
import {
  buscarListaPorEmpresa as buscarListaVentaPorEmpresa,
  buscarListaPorVendedor
} from '../../servicios/listaVentaService'
import { buscarExposicion, buscarTodasOptimizado } from '../../servicios/publicacionesService'
import ComponenteError from '../ComponenteError'
import styles from '../Publicaciones.module.css'

const ITEMS_PAGINA = 15

const Publicaciones = () => {
  const { loginDataContext: { idUsuario: idVendedor, empid } = {}, esPerfilAdmin } = useAuth()
  const idVendedorNormalizado = Number(idVendedor)
  const tieneIdVendedorValido = Number.isFinite(idVendedorNormalizado) && idVendedorNormalizado > 0
  const puedeBuscarComoAdmin = Boolean(esPerfilAdmin && empid)
  const imagenesFetchKeyRef = useRef('')
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [publicaciones, setPublicaciones] = useState([])
  const [cargando, setCargando] = useState(true)
  const [exposiciones, setExposiciones] = useState([])
  const [listasVentaConDatos, setListasVentaConDatos] = useState([])
  const [listasEnviosVencidas, setListasEnviosVencidas] = useState([])
  const [textoInput, setTextoInput] = useState('')
  const [textoBusqueda, setTextoBusqueda] = useState('')
  const [vistaCuadricula, setVistaCuadricula] = useState(false)
  const topRef = useRef(null)

  const {
    ordenamiento,
    categoriaSeleccionada,
    filtrosLocales,
    filtrosSeleccionados,
    caracteristicasActuales,
    mapEtiquetaIds
  } = useSelector((state) => state.filters)

  const aplicarFiltrosPublicaciones = (arrayAFiltrar, filtros) =>
    aplicarFiltrosGenerico(arrayAFiltrar, filtros, MAPA_PUBLICACIONES)

  const publicacionesSinFiltroCaracteristicas = useMemo(() => {
    return aplicarFiltrosPublicaciones(publicaciones, {
      ...filtrosLocales,
      categoriaSeleccionada,
      textoBusqueda
    })
  }, [publicaciones, filtrosLocales, categoriaSeleccionada, textoBusqueda])

  const publicacionesFiltradas = useMemo(() => {
    return aplicarFiltrosPublicaciones(publicacionesSinFiltroCaracteristicas, {
      filtrosSeleccionados,
      caracteristicasActuales,
      mapEtiquetaIds
    })
  }, [
    publicacionesSinFiltroCaracteristicas,
    filtrosSeleccionados,
    caracteristicasActuales,
    mapEtiquetaIds
  ])

  const publicacionesOrdenadas = useMemo(() => {
    return aplicarOrdenamiento(publicacionesFiltradas, ordenamiento)
  }, [publicacionesFiltradas, ordenamiento])

  const sinResultados = publicacionesOrdenadas.length === 0

  const { adjuntos, loadingImagenes, fetchImagenes } = useImagenesPublicacion()

  const scrollToTop = () => {
    if (typeof topRef.current?.scrollIntoView === 'function') {
      topRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const {
    itemsActual: publicacionesPaginadas,
    paginaActual,
    setPaginaActual,
    itemsPorPagina,
    setItemsPorPagina,
    totalPaginas
  } = usePaginacion(publicacionesOrdenadas, ITEMS_PAGINA)

  const idsPublicacionesParaImagenes = useMemo(
    () => publicacionesPaginadas.map((p) => p.idPublicacion),
    [publicacionesPaginadas]
  )

  const filtrarPorListaVencida = (listas) => {
    return listas.reduce((acc, lista) => {
      const estaVigente = validarRangoFechas(new Date(), lista.vigenciaHasta, 'day')
      if (!estaVigente) {
        acc.push({
          key: lista.idLista,
          value: lista.nombreLista.trim().toLowerCase(),
          label: lista.nombreLista,
          vigenciaHasta: lista.vigenciaHasta
        })
      }
      return acc
    }, [])
  }

  const fetchData = async () => {
    const empidAdmin = esPerfilAdmin ? empid : null
    const vendedorBusqueda = tieneIdVendedorValido ? idVendedorNormalizado : 0
    const { data } = await buscarTodasOptimizado(0, vendedorBusqueda, 0, '', empidAdmin)

    const listasVentas = esPerfilAdmin
      ? await buscarListaVentaPorEmpresa(empid)
      : await buscarListaPorVendedor(idVendedorNormalizado)
    const listasEnvios = esPerfilAdmin
      ? await buscarListaEnvioPorEmpresa(empid)
      : await buscarListaEnvioPorVendedor(idVendedorNormalizado)
    const listaEnviosUnificadas = Object.values(
      listasEnvios.data.reduce((acc, item) => {
        const key = item.nombreLista.trim().toLowerCase()
        if (!acc[key]) {
          acc[key] = {
            idLista: item.idLista,
            nombreLista: item.nombreLista,
            vigenciaDesde: item.vigenciaDesde,
            vigenciaHasta: item.vigenciaHasta,
            idVendedor: item.idVendedor,
            grilla: []
          }
        }

        if (Array.isArray(item.grilla)) {
          acc[key].grilla.push(...item.grilla)
        }

        return acc
      }, {})
    )

    const nombreListasEnUso = new Set(
      data.flatMap((pub) =>
        (pub.mediosEnvios ?? [])
          .filter(
            (m) =>
              m.idMedioEnvio === ENVIO_A_CARGO_COMPRADOR &&
              m.nombreLista &&
              pub.idPublicacionEstado === ESTADO_PUBLICADA
          )
          .map((m) => m.nombreLista.trim().toLowerCase())
      )
    )

    const listaEnviosEnUso = listaEnviosUnificadas.filter((l) =>
      nombreListasEnUso.has(l.nombreLista.trim().toLowerCase())
    )
    const listasEnvioVencidas = filtrarPorListaVencida(listaEnviosEnUso)
    setListasEnviosVencidas(listasEnvioVencidas)

    const listaIds = new Set(data.map((p) => p.idListaVenta))
    const listasVentasConDatos = listasVentas.data.filter((l) => listaIds.has(l.idListaVenta))
    setListasVentaConDatos(listasVentasConDatos)

    const exposicion = await buscarExposicion('')
    setExposiciones(exposicion.data)

    const publicaciones = data.map((pub) => {
      const listaMatch = listasVentas.data.find((l) => l.idListaVenta === pub.idListaVenta)
      const porcDctoPublicacion = Number(pub.porcDctoPublicacion ?? 0)
      const porcDctoLista = Number(pub.porcDctoLista ?? 0)
      let porcDctoMostrar = 0
      let origenDescuento = 'ninguno'
      if (porcDctoPublicacion > 0) {
        porcDctoMostrar = porcDctoPublicacion
        origenDescuento = 'publicacion'
      } else if (porcDctoLista > 0) {
        porcDctoMostrar = porcDctoLista
        origenDescuento = 'lista'
      }

      return {
        ...pub,
        porcDctoMostrar,
        origenDescuento,
        publicacionExposicion:
          exposicion.data.find((e) => e.idPublicacionExposicion === pub.idPublicacionExposicion)
            ?.nombre || '',
        listaVenta: listaMatch?.nombre || ''
      }
    })
    setPublicaciones(publicaciones)
    setCargando(false)
  }

  useEffect(() => {
    const campo = filtrosLocales.estadoPublicacion === ESTADO_VENCIDA ? 'hasta' : 'idPublicacion'
    dispatch(setOrdenamiento({ orden: 'DESC', campo }))
  }, [filtrosLocales.estadoPublicacion])

  useEffect(() => {
    if (idsPublicacionesParaImagenes.length === 0) {
      return
    }
    const fetchKey = idsPublicacionesParaImagenes.join(',')
    if (imagenesFetchKeyRef.current === fetchKey) {
      return
    }

    imagenesFetchKeyRef.current = fetchKey
    fetchImagenes({ idPublicaciones: idsPublicacionesParaImagenes })
  }, [idsPublicacionesParaImagenes])

  useEffect(() => {
    if (tieneIdVendedorValido || puedeBuscarComoAdmin) {
      fetchData()
    }
  }, [tieneIdVendedorValido, puedeBuscarComoAdmin, idVendedorNormalizado, empid, esPerfilAdmin])

  useEffect(() => {
    if (!esPerfilAdmin && filtrosLocales.usuarioVendedor) {
      dispatch(setFiltroLocal({ key: 'usuarioVendedor', value: '' }))
    }
  }, [esPerfilAdmin, filtrosLocales.usuarioVendedor])

  useEffect(() => {
    setPaginaActual(1)
    if (filtrosLocales.listaVenta !== '' || filtrosLocales.listaEnvioVencida !== '') {
      scrollToTop()
    }
  }, [publicacionesFiltradas])

  const handleToggle = (_event, _publicacion, idPublicacion) => {
    navigate(`/Publicaciones/Administrar/${idPublicacion}`)
    window.scrollTo(0, 0)
  }

  const contextoFiltros = useMemo(
    () => ({ exposiciones, listasVentaConDatos, listasEnviosVencidas, publicaciones }),
    [exposiciones, listasVentaConDatos, listasEnviosVencidas, publicaciones]
  )

  const filtrosVisibles = useMemo(() => {
    const campos = [
      'categoria',
      'exposicion',
      'listaVenta',
      'estadoPublicacion',
      'listaEnvioVencida'
    ]
    return esPerfilAdmin ? [...campos, 'usuarioVendedor'] : campos
  }, [esPerfilAdmin])

  return (
    <div className={styles.layout} ref={topRef}>
      {publicaciones.length > 0 && (
        <FiltrosContainer
          arrayAFiltrar={publicacionesSinFiltroCaracteristicas}
          mapaFiltros={MAPA_PUBLICACIONES}
          contextoFiltros={contextoFiltros}
          filtrosVisibles={filtrosVisibles}
          buscador={{
            onChange: setTextoBusqueda,
            placeholder: MAPA_PUBLICACIONES.textoBusqueda.placeholder,
            valor: textoInput,
            setBusqueda: setTextoInput
          }}
        />
      )}
      <fieldset className={`fieldset ${styles.fieldset}`}>
        <div className={styles.headerPublicaciones}>
          <legend>Administrar Publicaciones</legend>
          <div className={styles.accionesVista}>
            <OrdenarPor
              options={[
                {
                  label: 'Más recientes',
                  value: { orden: 'DESC', campo: 'idPublicacion' }
                },
                {
                  label: 'Más antiguos',
                  value: { orden: 'ASC', campo: 'idPublicacion' }
                },
                ...(filtrosLocales.estadoPublicacion === ESTADO_VENCIDA
                  ? [
                      {
                        label: 'Vencidas más recientes',
                        value: { orden: 'DESC', campo: 'hasta' }
                      }
                    ]
                  : [])
              ]}
            />
            <VistaCuadricula
              requiereCuadricula={true}
              vistaCuadricula={vistaCuadricula}
              setVistaCuadricula={setVistaCuadricula}
            />
          </div>
        </div>
        {cargando ? (
          <div className="d-flex justify-content-center p-4">
            <Spinner animation="border" role="status" aria-label="Cargando" />
          </div>
        ) : !sinResultados ? (
          <>
            <div className={vistaCuadricula ? styles.gridContainer : ''}>
              {publicacionesPaginadas.map((publicacion) => (
                <div
                  key={publicacion.idPublicacion}
                  className={vistaCuadricula ? '' : styles.publicacion}
                >
                  <Publicacion
                    vieneDeAdministrar={true}
                    onToggleClick={handleToggle}
                    publicacion={publicacion}
                    vistaCuadricula={vistaCuadricula}
                    adjunto={adjuntos.find(
                      (a) => a.idPublicacion === Number(publicacion.idPublicacion)
                    )}
                    loadingImagenes={loadingImagenes}
                  />
                </div>
              ))}
            </div>
            <Paginacion
              totalPaginas={totalPaginas}
              paginaActual={paginaActual}
              onCambioPagina={(num) => {
                setPaginaActual(num)
                scrollToTop()
              }}
              itemsPorPagina={itemsPorPagina}
              onCambioItemsPorPagina={(num) => {
                setItemsPorPagina(num)
                setPaginaActual(1)
                scrollToTop()
              }}
            />
          </>
        ) : (
          <div className="row">
            <ComponenteError tipo="I" idAviso={8} key={2} />
          </div>
        )}
      </fieldset>
    </div>
  )
}

export default Publicaciones
